import { useBooleanState } from "@repo/components/src/hooks/states";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import {
  Input,
  InputField,
  InputIcon,
  InputSlot,
} from "@repo/components/src/ui/input";
import { AlertCircleIcon, EyeIcon, EyeOffIcon } from "lucide-react-native";
import { ComponentProps } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";

export type FormInputPasswordProps<TFieldValues extends FieldValues> =
  FormInputProps<TFieldValues, string> & {
    inputProps?: ComponentProps<typeof Input>;
  };

enum TextTypeEnum {
  Text = "text",
  Password = "password",
}

export default function FormInputPassword<TFieldValues extends FieldValues>(
  props: FormInputPasswordProps<TFieldValues>,
) {
  const {
    control,
    name,
    placeholder,
    label,
    required,
    disabled,
    className,
    size,
    inputProps = {},
  } = props;
  const { state: hidePassword, toggle: togglePassword } = useBooleanState(true);
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
        <FormControl
          isInvalid={!!error}
          isRequired={required}
          isDisabled={disabled}
          className={className}
          size={size}
        >
          <FormControlLabel>
            <FormControlLabelText>{label}</FormControlLabelText>
          </FormControlLabel>
          <Input
            className="my-1"
            isDisabled={disabled}
            isInvalid={!!error}
            size={size}
            {...inputProps}
          >
            <InputField
              ref={focusScrollRef(ref)}
              placeholder={placeholder}
              value={value || ""}
              onChangeText={onChange}
              type={hidePassword ? TextTypeEnum.Password : TextTypeEnum.Text}
            />
            <InputSlot className="pr-3" onPress={togglePassword}>
              <InputIcon as={hidePassword ? EyeIcon : EyeOffIcon} />
            </InputSlot>
          </Input>
          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
            <FormControlErrorText>{error?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
